import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Gift, Heart, TrendingUp, CheckCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { SimpleLayout } from '@/components/layout/SimpleLayout'

interface InvitationData {
  leadId: string
  tenantName: string
  tenantId: string
  firstName: string
  alreadyConverted: boolean
}

export default function InvitationLandingPage() {
  const { inviteToken } = useParams<{ inviteToken: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const validateInvitation = async () => {
      if (!inviteToken) {
        setError('Kein Einladungs-Token gefunden')
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        // Fetch lead data by invite token (no tenants join to avoid RLS)
        const { data: leadData, error: leadError } = await supabase
          .from('referral_leads')
          .select('id, first_name, tenant_id, status, converted_to_customer_id')
          .eq('invite_token', inviteToken)
          .maybeSingle()

        if (leadError || !leadData) {
          console.error('Lead fetch error:', leadError)
          setError('Einladung nicht gefunden oder ungültig')
          setLoading(false)
          return
        }

        // Check if already converted
        if (leadData.status === 'kunde' || leadData.converted_to_customer_id) {
          setInvitationData({
            leadId: leadData.id,
            tenantName: 'Ihr Unternehmen',
            tenantId: leadData.tenant_id,
            firstName: leadData.first_name,
            alreadyConverted: true
          })
          setLoading(false)
          return
        }

        setInvitationData({
          leadId: leadData.id,
          tenantName: 'Ihr Unternehmen',
          tenantId: leadData.tenant_id,
          firstName: leadData.first_name,
          alreadyConverted: false
        })

        // Log access to audit
        await supabase.from('audit_logs').insert({
          action: 'invitation_landing_page_accessed',
          resource_type: 'referral_invitation',
          resource_id: leadData.id,
          details: {
            invite_token: inviteToken,
            tenant_id: leadData.tenant_id
          },
          severity: 'info',
          source: 'invitation_landing_page'
        })

      } catch (err) {
        console.error('Error validating invitation:', err)
        setError('Ein Fehler ist aufgetreten')
      } finally {
        setLoading(false)
      }
    }

    validateInvitation()
  }, [inviteToken])

  const handleRegisterClick = () => {
    if (!inviteToken) return
    navigate(`/registrieren?invite=${inviteToken}`)
  }

  if (loading) {
    return (
      <SimpleLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Einladung wird geladen...</p>
            </CardContent>
          </Card>
        </div>
      </SimpleLayout>
    )
  }

  if (error || !invitationData) {
    return (
      <SimpleLayout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Einladung ungültig</CardTitle>
              <CardDescription>
                {error || 'Die Einladung konnte nicht gefunden werden.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/')} variant="outline" className="w-full">
                Zur Startseite
              </Button>
            </CardContent>
          </Card>
        </div>
      </SimpleLayout>
    )
  }

  if (invitationData.alreadyConverted) {
    return (
      <SimpleLayout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-6 w-6 text-green-500" />
                <CardTitle>Bereits registriert</CardTitle>
              </div>
              <CardDescription>
                Sie haben sich bereits als Unterstützer registriert!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={() => navigate('/anmelden')} className="w-full">
                Jetzt anmelden
              </Button>
            </CardContent>
          </Card>
        </div>
      </SimpleLayout>
    )
  }

  return (
    <SimpleLayout>
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-background py-12 px-4">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Hero Section */}
          <div className="text-center space-y-4">
            <Badge variant="secondary" className="mb-4">
              Persönliche Einladung
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Willkommen, {invitationData.firstName}!
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              <strong>{invitationData.tenantName}</strong> lädt Sie ein, Teil der Unterstützer-Community zu werden.
            </p>
          </div>

          {/* Benefits Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Card className="border-primary/20 hover:border-primary/40 transition-all">
              <CardHeader>
                <Gift className="h-10 w-10 text-primary mb-2" />
                <CardTitle className="text-lg">Exklusive Prämien</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Sammeln Sie Stempel bei jedem Besuch und lösen Sie attraktive Prämien ein.
                </p>
              </CardContent>
            </Card>

            <Card className="border-primary/20 hover:border-primary/40 transition-all">
              <CardHeader>
                <Heart className="h-10 w-10 text-primary mb-2" />
                <CardTitle className="text-lg">Soziales Engagement</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Jede Ihrer Aktionen unterstützt soziale Projekte und hilft Menschen in Not.
                </p>
              </CardContent>
            </Card>

            <Card className="border-primary/20 hover:border-primary/40 transition-all">
              <CardHeader>
                <TrendingUp className="h-10 w-10 text-primary mb-2" />
                <CardTitle className="text-lg">Empfehlungsbonus</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Empfehlen Sie Freunde weiter und erhalten Sie zusätzliche Stempel und Vorteile.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* CTA Section */}
          <Card className="border-primary bg-gradient-to-br from-primary/5 to-primary/10">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Jetzt kostenlos registrieren</CardTitle>
              <CardDescription>
                Die Registrierung dauert nur 2 Minuten und ist völlig kostenlos.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-4">
              <Button 
                size="lg" 
                onClick={handleRegisterClick}
                className="w-full max-w-md bg-gradient-to-r from-primary to-primary-glow hover:shadow-glow transition-all duration-300"
              >
                Jetzt Unterstützer werden
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Mit der Registrierung akzeptieren Sie unsere Datenschutzbestimmungen
              </p>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <div className="text-center text-sm text-muted-foreground mt-8">
            <p>
              Diese Einladung wurde Ihnen von <strong>{invitationData.tenantName}</strong> gesendet.
            </p>
          </div>
        </div>
      </div>
    </SimpleLayout>
  )
}
